import {useCallback, useEffect, useState} from 'react';
import {
  BrowserRouter as Router,
  Switch,
  Route
} from 'react-router-dom';
import IdleTimer from 'react-idle-timer';
import {Header} from './components/header/Header';
import NotFound from './components/base/errors/404';
import {useAppDispatch} from './store/hooks';
import {MsalAuthenticationTemplate,
  useMsal,
  useAccount,
  MsalProvider} from '@azure/msal-react';
import {InteractionType,
  InteractionStatus,
  InteractionRequiredAuthError,
  IPublicClientApplication} from '@azure/msal-browser';
import {handlePolicyChange, loginRequest} from './services/auth-provider';
import {inactivityTimeout} from './constants/constants';
import EnsurePermission from './components/common/EnsurePermission';
import {RootState} from './store/store';
import {useAppSelector} from './store/hooks';
import {
  setPermissions, setActiveTenant, setTenantReports
} from './store/permissions/permissions.slice';
import usePermissions from './hooks/usePermissions';
import {routes} from './constants/routes';
import LogoutMessage from './components/App/LogoutMessage';
import {getTenantFromRoute} from './helpers';
import {logoutUser} from './api/general';
import Spinner from './components/common/Spinner';
import LockedUser from './components/App/LockedUser';
import {setUserDetails} from './store/currentUser/currentUser.slice';
import {ThemeProvider} from '@mui/material';
import {theme} from './constants/theme';
import SnackbarProvider from './components/snackbar';
import {getReports} from './api/reports';
import {useSnackbarHook} from './hooks/useSnackbar';

type AppProps = {
  pca: IPublicClientApplication
};

const ProtectedContent = () => {
  const dispatch = useAppDispatch();
  const {instance, accounts, inProgress} = useMsal();
  const account = useAccount(accounts[0] || {});
  const [isReportsLoading, setTenantReportsLoading] = useState(true);
  const [atsResponse, setAtsResponse] = useState<any>(null);
  const {pathname} = window.location;
  const {
    user: {isLocked: isUserLocked},
    permissions: {activeTenant}
  } = useAppSelector((state: RootState) => state);

  const {addErrorToast} = useSnackbarHook();

  if (pathname.includes('login')) {
    const tenantSlug = getTenantFromRoute(pathname);

    if (tenantSlug) {
      dispatch(setActiveTenant(tenantSlug));
    }
  }

  useEffect(() => {
    if (!atsResponse && account && inProgress === InteractionStatus.None) {
      const request = {
        ...loginRequest,
        account
      };
      instance.handleRedirectPromise().then((response) => {
        handlePolicyChange(response, instance);
      });
      instance.acquireTokenSilent(request).then((response) => {
        setAtsResponse(response);
      }).catch((e) => {
        if (e instanceof InteractionRequiredAuthError) {
          instance.acquireTokenRedirect(request);
        } else {
          instance.logoutRedirect();
        }
      });
    }
  }, [account, inProgress, instance, atsResponse]);

  const [
    permissionsData, isPermissionsFetching, fetchPermissionsError
  ] = usePermissions();

  useEffect(() => {
    if (permissionsData) {
      dispatch(setPermissions(permissionsData));
      dispatch(setUserDetails({
        adId: permissionsData.adId,
        firstName: permissionsData.firstName,
        lastName: permissionsData.lastName,
        email: permissionsData.email,
        groups: permissionsData.groups,
        id: permissionsData.userId,
        slug: permissionsData.userSlug
      }));
    }
  }, [permissionsData?.activeTenant]);

  const getTenantReports = useCallback(async () => {
    if (!activeTenant) return;

    setTenantReportsLoading(true);

    try {
      const reports = await getReports();

      dispatch(setTenantReports({reports}));
    } catch (e) {
      addErrorToast(e?.message);
    } finally {
      setTenantReportsLoading(false);
    }
  }, [activeTenant]);

  useEffect(() => {
    getTenantReports();
  }, [getTenantReports]);

  if (isUserLocked) {
    return (
      <LockedUser instance={instance}/>
    );
  }

  if (fetchPermissionsError) {
    const errorStatus = fetchPermissionsError.status;

    if (errorStatus === 401 || errorStatus === 403) {
      return (<LogoutMessage instance={instance} />);
    }
  }

  return ( atsResponse ?
    <IdleTimer
      element={document}
      onIdle={async () => {
        await logoutUser();
        instance.logout({
          postLogoutRedirectUri: `${window.location.href}`
        });
      }}
      timeout={inactivityTimeout}
    >
      <>
        <Router>
          <div className="root-wrapper">
            <Header />

            {isPermissionsFetching || isReportsLoading ? (
                <Spinner loadingText="Switching to a tenant" />
              ) : (
                <Switch>
                  {routes.map(({path, component, isExact, permissions}) => {
                    const RouteComponent = component;
                    const showError = true;

                    return (
                      <Route
                        key={path}
                        path={path}
                        exact={isExact}
                      >
                        <EnsurePermission
                          elementPermissions={permissions}
                          showError={showError}>
                          <RouteComponent />
                        </EnsurePermission>
                      </Route>
                    );
                  })}

                  <Route path="*" component={NotFound}/>
                </Switch>
            )}
          </div>
        </Router>
      </>
    </IdleTimer> :
    null
  );
};

const App = ({pca}: AppProps) => (
  <MsalProvider instance={pca}>
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={loginRequest}
    >
      <ThemeProvider theme={theme}>
        <SnackbarProvider>
          <ProtectedContent />
        </SnackbarProvider>
      </ThemeProvider>
    </MsalAuthenticationTemplate>
  </MsalProvider>
);

export default App;
